import { format, parseISO } from 'date-fns';

export function formatEventDateTime(dateTime: string, timezone: string): string {
  // The datetime is stored as local time in the event's timezone
  // We need to display it as-is, not convert it
  const date = parseISO(dateTime);
  
  // Format the date and time
  const formatted = format(date, 'PPPP • p');
  
  // Get a short timezone display
  const tzDisplay = getTimezoneDisplay(timezone);
  
  return `${formatted} (${tzDisplay})`;
}

// Simple date formatting (from dateUtils.ts)
export function formatDate(dateTime: string): string {
  const date = parseISO(dateTime);
  return format(date, 'PP'); // Uses date-fns for consistency
}

// Relative time formatting (from dateUtils.ts)
export function getRelativeTime(dateTime: string): string {
  const date = parseISO(dateTime);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.round(diffSecs / 60);
  const diffHours = Math.round(diffMins / 60);
  const diffDays = Math.round(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return formatDate(dateTime);
}

export function getTimezoneDisplay(timezone: string): string {
  // Map common timezones to abbreviations
  const tzMap: { [key: string]: string } = {
    'Europe/London': 'GMT/BST',
    'Europe/Paris': 'CET/CEST',
    'Europe/Berlin': 'CET/CEST',
    'America/New_York': 'EST/EDT',
    'America/Chicago': 'CST/CDT',
    'America/Denver': 'MST/MDT',
    'America/Los_Angeles': 'PST/PDT',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Hong_Kong': 'HKT',
    'Asia/Singapore': 'SGT',
    'Asia/Dubai': 'GST',
    'Asia/Kolkata': 'IST',
    'Australia/Sydney': 'AEDT/AEST',
    'Australia/Melbourne': 'AEDT/AEST',
    'Pacific/Auckland': 'NZDT/NZST',
    'UTC': 'UTC',
  };
  
  return tzMap[timezone] || timezone.split('/').pop() || timezone;
}

export function formatTimeOnly(dateTime: string): string {
  const date = parseISO(dateTime);
  return format(date, 'p');
}

export function formatEventDateTimeCompact(dateTime: string, timezone: string): string {
  // For compact display on cards
  const date = parseISO(dateTime);
  
  // Format: "Dec 15, 2024 • 7:00 PM (GMT)"
  const formatted = format(date, 'MMM d, yyyy • p');
  
  // Get a short timezone display
  const tzDisplay = getTimezoneDisplay(timezone);
  
  return `${formatted} (${tzDisplay})`;
}